import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, ChevronRight, ChevronDown, Settings2, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const PLATFORM_COLORS = {
  tradelocker: '#FF6B6B',
  metaapi:     '#2979FF',
  matchtrader: '#FFB000',
  tradovate:   '#00FF94',
  funded:      '#FFB000',
};

const MULTIPLIERS = [0.25, 0.5, 0.75, 1.0, 1.5, 2.0];

const INPUT = { width: '100%', background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '9px 12px', fontSize: '13px', outline: 'none', borderRadius: 0, fontFamily: 'inherit' };
const LABEL = { display: 'block', fontSize: '10px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--pp-text-3)', marginBottom: '5px' };

// Expandable-trade detail field
function Detail({ label, value, mono = false, small = false }) {
  return (
    <div>
      <p style={{ margin: 0, fontSize: 9, color: 'var(--pp-text-3)', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</p>
      <p className={mono ? 'mono' : ''} style={{ margin: '2px 0 0', fontSize: small ? 10 : 11, color: 'var(--pp-text)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</p>
    </div>
  );
}

// ── Day-of-week copy schedule modal ──────────────────────────────────────────
const DAY_LABELS = [ ['mon','Mon'], ['tue','Tue'], ['wed','Wed'], ['thu','Thu'], ['fri','Fri'], ['sat','Sat'], ['sun','Sun'] ];
const COMMON_TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Los_Angeles',
  'Europe/London', 'Europe/Berlin', 'Europe/Madrid', 'Asia/Dubai',
  'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney',
];
function CopyScheduleModal({ onClose, accounts }) {
  const [schedules, setSchedules]   = useState([]);  // [{account_id, days, timezone, enabled}]
  const [loading, setLoading]       = useState(true);
  const [busy, setBusy]             = useState(false);
  const [selectedTarget, setSelectedTarget] = useState('default'); // 'default' | accountId
  const [form, setForm] = useState({ enabled: true, days: { mon:true, tue:true, wed:true, thu:true, fri:true, sat:false, sun:false }, timezone: 'UTC' });

  useEffect(() => {
    (async () => {
      try { setSchedules(await api.listCopySchedules() || []); }
      catch (e) { toast.error(e.message); }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    const existing = schedules.find(s => s.account_id === selectedTarget);
    if (existing) {
      setForm({
        enabled: existing.enabled !== false,
        days: { mon:true, tue:true, wed:true, thu:true, fri:true, sat:false, sun:false, ...(existing.days || {}) },
        timezone: existing.timezone || 'UTC',
      });
    } else {
      setForm({ enabled: true, days: { mon:true, tue:true, wed:true, thu:true, fri:true, sat:false, sun:false }, timezone: 'UTC' });
    }
  }, [selectedTarget, schedules]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.upsertCopySchedule(selectedTarget, form);
      toast.success(`Schedule saved for ${selectedTarget === 'default' ? 'all accounts (default)' : selectedTarget}`);
      const next = await api.listCopySchedules();
      setSchedules(next || []);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const removeSchedule = async () => {
    if (!window.confirm(`Remove schedule for ${selectedTarget === 'default' ? 'default (all accounts)' : selectedTarget}?\n\nWithout a schedule the account will copy every day.`)) return;
    setBusy(true);
    try {
      await api.deleteCopySchedule(selectedTarget);
      toast.success('Schedule removed');
      const next = await api.listCopySchedules();
      setSchedules(next || []);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 14 }} onClick={onClose}>
      <div data-testid="copy-schedule-modal" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-accent)', padding: 22, width: 540, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--pp-accent)' }}>COPY SCHEDULE — WHICH DAYS TO MIRROR</p>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 11, color: 'var(--pp-text-3)' }}>Pick which days of the week each account should copy trades. The "Default" applies to every account that doesn't have its own schedule.</p>

        <div style={{ marginBottom: 12 }}>
          <label style={LABEL}>Target Account</label>
          <select data-testid="schedule-target-select" style={{ ...INPUT, cursor: 'pointer' }} value={selectedTarget} onChange={e => setSelectedTarget(e.target.value)}>
            <option value="default">★ Default — applies to all accounts</option>
            {(accounts || []).map(a => (
              <option key={a.account_id} value={a.account_id}>{a.account_label || a.account_id} · {a.platform}</option>
            ))}
          </select>
          {schedules.find(s => s.account_id === selectedTarget) && (
            <p style={{ margin: '6px 0 0', fontSize: 10, color: '#00FF94' }}>● Schedule exists for this target</p>
          )}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer', fontSize: 12 }}>
          <input data-testid="schedule-enabled-toggle" type="checkbox" checked={form.enabled} onChange={e => setForm(p => ({ ...p, enabled: e.target.checked }))} />
          Enforce this schedule (off = copy every day regardless)
        </label>

        <label style={LABEL}>Days to copy</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 14 }}>
          {DAY_LABELS.map(([key, label]) => (
            <button
              key={key} type="button"
              data-testid={`schedule-day-${key}`}
              disabled={!form.enabled}
              onClick={() => setForm(p => ({ ...p, days: { ...p.days, [key]: !p.days[key] } }))}
              style={{ height: 38, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', cursor: form.enabled ? 'pointer' : 'not-allowed',
                background: form.days[key] ? 'rgba(0,255,148,0.18)' : 'transparent',
                border: `1px solid ${form.days[key] ? '#00FF94' : 'var(--pp-border)'}`,
                color: form.days[key] ? '#00FF94' : 'var(--pp-text-3)',
                opacity: form.enabled ? 1 : 0.4 }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={LABEL}>Timezone</label>
          <select data-testid="schedule-tz-select" style={{ ...INPUT, cursor: 'pointer' }} value={form.timezone} onChange={e => setForm(p => ({ ...p, timezone: e.target.value }))}>
            {COMMON_TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
          </select>
          <p style={{ margin: '4px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>"Monday" is evaluated in this timezone. Useful so trades fire on YOUR Mon-Fri, not server's.</p>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <button onClick={onClose} style={{ flex: 1, height: 40, background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', cursor: 'pointer' }}>Cancel</button>
          {schedules.find(s => s.account_id === selectedTarget) && (
            <button data-testid="schedule-remove-btn" onClick={removeSchedule} disabled={busy} style={{ flex: 1, height: 40, background: 'transparent', border: '1px solid var(--pp-error)', color: 'var(--pp-error)', cursor: 'pointer', fontWeight: 700 }}>REMOVE</button>
          )}
          <button data-testid="schedule-save-btn" onClick={save} disabled={busy || loading} style={{ flex: 2, height: 40, background: 'var(--pp-accent)', border: 'none', color: '#000', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em' }}>{busy ? 'SAVING…' : 'SAVE SCHEDULE'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Per-account × per-strategy risk settings panel ────────────────────────────
function StrategyRiskPanel({ accountId, strat, onSave }) {
  const [form, setForm] = useState({
    lot_size_mode:  strat.lot_size_mode  || 'scale',
    fixed_lot:      strat.fixed_lot      || 0.01,
    risk_multiplier:strat.risk_multiplier|| 1.0,
    max_daily_loss: strat.max_daily_loss || 100,
    enabled:        strat.enabled !== false,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateAccountStrategySettings(accountId, strat.strategy_id, form);
      toast.success(`Risk settings saved for ${strat.strategy_name}!`);
      onSave(strat.strategy_id, form);
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ padding: '14px 16px', background: 'var(--pp-overlay)', borderTop: '1px solid var(--pp-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{strat.strategy_name}</p>
          <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>{strat.instrument}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 9, color: form.enabled ? 'var(--pp-success)' : 'var(--pp-error)', fontWeight: 700 }}>
            {form.enabled ? 'COPYING' : 'PAUSED'}
          </span>
          <button onClick={() => setForm(p => ({ ...p, enabled: !p.enabled }))}
            style={{ width: 32, height: 18, background: form.enabled ? 'rgba(0,255,148,0.3)' : 'var(--pp-border)', border: `1px solid ${form.enabled ? 'var(--pp-success)' : 'var(--pp-border-active)'}`, borderRadius: 9, cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', padding: '0 3px' }}>
            <div style={{ width: 12, height: 12, borderRadius: '50%', background: form.enabled ? 'var(--pp-success)' : 'var(--pp-text-3)', transition: 'transform 150ms', transform: `translateX(${form.enabled ? 14 : 0}px)` }} />
          </button>
        </div>
      </div>

      {/* Risk multiplier chips */}
      <div style={{ marginBottom: 12 }}>
        <label style={LABEL}>Risk Multiplier</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {MULTIPLIERS.map(m => (
            <button key={m} onClick={() => setForm(p => ({ ...p, risk_multiplier: m }))}
              style={{ padding: '6px 10px', background: form.risk_multiplier === m ? (m > 1 ? '#FFB000' : 'var(--pp-accent)') : 'transparent', border: `1px solid ${form.risk_multiplier === m ? (m > 1 ? '#FFB000' : 'var(--pp-accent)') : 'var(--pp-border)'}`, color: form.risk_multiplier === m ? '#000' : 'var(--pp-text-2)', fontSize: 12, fontWeight: form.risk_multiplier === m ? 700 : 400, cursor: 'pointer' }}>
              {m}×
            </button>
          ))}
        </div>
        <p style={{ margin: '5px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>
          {form.risk_multiplier}× = {form.risk_multiplier < 1 ? 'reduced' : form.risk_multiplier > 1 ? 'increased' : 'standard'} copy size for this account
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div>
          <label style={LABEL}>Lot Size Mode</label>
          <select style={{ ...INPUT, cursor: 'pointer' }} value={form.lot_size_mode} onChange={e => setForm(p => ({ ...p, lot_size_mode: e.target.value }))}>
            <option value="scale">Equity Ratio (Recommended)</option>
            <option value="fixed">Fixed Lot</option>
          </select>
        </div>
        {form.lot_size_mode === 'fixed' && (
          <div>
            <label style={LABEL}>Fixed Lot</label>
            <input style={INPUT} type="number" step="0.01" min="0.01" value={form.fixed_lot}
              onChange={e => setForm(p => ({ ...p, fixed_lot: parseFloat(e.target.value) || 0.01 }))} />
          </div>
        )}
        <div>
          <label style={LABEL}>Max Daily Loss ($)</label>
          <input style={INPUT} type="number" value={form.max_daily_loss}
            onChange={e => setForm(p => ({ ...p, max_daily_loss: parseFloat(e.target.value) || 100 }))} />
        </div>
      </div>

      <button onClick={save} disabled={saving} className="btn-primary" style={{ width: '100%', height: 42, fontSize: 12 }}>
        {saving ? 'SAVING...' : `SAVE RISK SETTINGS — ${strat.strategy_name}`}
      </button>
    </div>
  );
}

// ── Account card with expandable strategy risk panels ─────────────────────────
function AccountCard({ account, onUpdate }) {
  const [expanded, setExpanded]       = useState(false);
  const [openStratId, setOpenStratId] = useState(null);
  const [strats, setStrats]           = useState(account.strategy_settings || []);

  const color  = PLATFORM_COLORS[account.platform_key] || 'var(--pp-text-3)';
  const initials = account.platform.slice(0, 2).toUpperCase();

  const handleSave = (stratId, newSettings) => {
    setStrats(prev => prev.map(s => s.strategy_id === stratId ? { ...s, ...newSettings } : s));
  };

  return (
    <div style={{ background: 'var(--pp-surface)', border: `1px solid ${account.status === 'connected' || account.status === 'active' ? 'rgba(0,255,148,0.2)' : 'var(--pp-border)'}`, marginBottom: 10 }}>
      {/* Account header — tap to expand */}
      <button onClick={() => setExpanded(!expanded)}
        style={{ width: '100%', background: 'none', border: 'none', padding: '14px 16px', textAlign: 'left', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 38, height: 38, background: `${color}20`, border: `1px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 12, fontWeight: 800, color, fontFamily: 'monospace' }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{account.account_label}</p>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>
              {account.platform} · {account.server}
              {account.is_demo && <span style={{ marginLeft: 6, fontSize: 9, color: '#FFB000', fontWeight: 700 }}>DEMO</span>}
              {account.is_funded && <span style={{ marginLeft: 6, fontSize: 9, color: '#FFB000', fontWeight: 700, padding: '1px 5px', border: '1px solid rgba(255,176,0,0.4)', background: 'rgba(255,176,0,0.1)' }}>FUNDED</span>}
              {account.needs_link && <span style={{ marginLeft: 6, fontSize: 9, color: 'var(--pp-error)', fontWeight: 700 }}>LINK MT5</span>}
            </p>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            {account.balance != null && account.balance > 0 && (
              <p className="mono" style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>${Number(account.balance).toLocaleString()}</p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end', marginTop: 2 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: account.status === 'connected' || account.status === 'active' ? 'var(--pp-success)' : '#FFB000' }} />
              <span style={{ fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em' }}>{(account.status || 'connecting').toUpperCase()}</span>
            </div>
          </div>
          <ChevronDown size={16} color="var(--pp-text-3)" style={{ transition: 'transform 150ms', transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }} />
        </div>
        {/* Strategy count badge */}
        {strats.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            {strats.map(s => (
              <span key={s.strategy_id} style={{ fontSize: 9, padding: '3px 8px', background: s.enabled !== false ? 'rgba(0,255,148,0.12)' : 'rgba(255,51,51,0.12)', color: s.enabled !== false ? 'var(--pp-success)' : 'var(--pp-error)', fontWeight: 700, letterSpacing: '0.05em' }}>
                {s.enabled !== false ? '▶' : '⏸'} {s.strategy_name} {s.risk_multiplier !== 1 ? `(${s.risk_multiplier}×)` : ''}
              </span>
            ))}
          </div>
        )}
      </button>

      {/* Expanded — per-strategy risk panels */}
      {expanded && (
        <div>
          {strats.length === 0 ? (
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--pp-border)', textAlign: 'center', color: 'var(--pp-text-3)', fontSize: 13 }}>
              No strategies linked to this account yet. Subscribe to a strategy first.
            </div>
          ) : (
            strats.map(strat => (
              <div key={strat.strategy_id}>
                {/* Strategy row — tap to open risk settings */}
                <button onClick={() => setOpenStratId(openStratId === strat.strategy_id ? null : strat.strategy_id)}
                  style={{ width: '100%', background: openStratId === strat.strategy_id ? 'rgba(41,121,255,0.06)' : 'transparent', border: 'none', borderTop: '1px solid var(--pp-border)', padding: '12px 16px', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Settings2 size={16} color="var(--pp-text-3)" />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{strat.strategy_name}</p>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)' }}>{strat.instrument} · {strat.risk_multiplier}× risk · {strat.lot_size_mode}</p>
                  </div>
                  <ChevronRight size={14} color="var(--pp-text-3)" style={{ transition: 'transform 150ms', transform: openStratId === strat.strategy_id ? 'rotate(90deg)' : 'rotate(0)' }} />
                </button>
                {openStratId === strat.strategy_id && (
                  <StrategyRiskPanel accountId={account.account_id} strat={strat} onSave={handleSave} />
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Strategy-grouped panel: each subscribed strategy lists ALL of the
// student's linked accounts with per-account toggle + risk multiplier.
// Default-off per the product spec: students must explicitly opt-in each
// account they want a given strategy to copy onto. The strategy-level
// "PAUSE ALL" button cascades to every account at once.
function StrategyGroup({ group }) {
  const [rows, setRows] = useState(group.rows.map(r => ({
    accountId:     r.acct.account_id,
    accountLabel:  r.acct.account_label,
    brokerName:    r.acct.broker_name,
    subAccountId:  r.acct.sub_account_id,
    platform:      r.acct.platform,
    platformKey:   r.acct.platform_key,
    isDemo:        r.acct.is_demo,
    enabled:       !!r.s.enabled,
    risk:          Number(r.s.risk_multiplier ?? 1.0),
    strategyId:    r.s.strategy_id,
    saving:        false,
  })));
  const [expanded, setExpanded] = useState(false);

  const anyOn = rows.some(r => r.enabled);
  const allOn = rows.every(r => r.enabled);

  const persist = async (rowIdx, patch) => {
    const row = rows[rowIdx];
    setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, ...patch, saving: true } : r));
    try {
      await api.updateAccountStrategySettings(row.accountId, row.strategyId, {
        enabled: patch.enabled ?? row.enabled,
        risk_multiplier: patch.risk ?? row.risk,
      });
    } catch (e) {
      toast.error(e.message);
      // Roll back
      setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, enabled: row.enabled, risk: row.risk, saving: false } : r));
      return;
    }
    setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, saving: false } : r));
  };

  const pauseAll = async () => {
    const targetState = !allOn;  // If all on → pause all. Otherwise → resume all.
    setRows(prev => prev.map(r => ({ ...r, saving: true })));
    await Promise.all(rows.map(r => api.updateAccountStrategySettings(r.accountId, r.strategyId, {
      enabled: targetState, risk_multiplier: r.risk,
    }).catch(() => {})));
    setRows(prev => prev.map(r => ({ ...r, enabled: targetState, saving: false })));
    toast.success(`${group.strategy_name} ${targetState ? 'resumed' : 'paused'} on all accounts`);
  };

  return (
    <div data-testid={`strategy-group-${group.strategy_id}`} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', marginBottom: 12 }}>
      <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{group.strategy_name}</p>
          <p style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--pp-text)' }}>
            {group.instrument || 'Multi-instrument'} · {rows.filter(r => r.enabled).length} of {rows.length} accounts copying
          </p>
        </div>
        <button data-testid={`strategy-pause-all-${group.strategy_id}`} onClick={(e) => { e.stopPropagation(); pauseAll(); }}
          style={{ background: anyOn ? 'transparent' : 'rgba(0,255,148,0.15)',
                   border: `1px solid ${anyOn ? 'var(--pp-warning)' : 'var(--pp-success)'}`,
                   color: anyOn ? 'var(--pp-warning)' : 'var(--pp-success)',
                   fontSize: 10, padding: '6px 12px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em' }}>
          {anyOn ? '⏸ PAUSE ALL' : '▶ RESUME ALL'}
        </button>
        {expanded ? <ChevronDown size={16} color="var(--pp-text-3)" /> : <ChevronRight size={16} color="var(--pp-text-3)" />}
      </div>
      {expanded && (
        <div style={{ borderTop: '1px solid var(--pp-border)' }}>
          {rows.map((r, i) => (
            <div key={r.accountId} data-testid={`strategy-acct-${group.strategy_id}-${r.accountId}`} style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: i < rows.length - 1 ? '1px solid rgba(39,39,42,0.4)' : 'none' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--pp-accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.accountLabel || r.accountId}</p>
                <p style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--pp-text)' }}>
                  {r.brokerName ? `${r.brokerName} · ` : ''}{r.platform}{r.isDemo ? ' · DEMO' : ''}
                  {r.subAccountId && r.subAccountId !== r.accountLabel && (
                    <span style={{ marginLeft: 6, color: 'var(--pp-accent)', fontWeight: 600 }}>· {r.subAccountId}</span>
                  )}
                </p>
              </div>
              {/* Per-account risk multiplier */}
              <select data-testid={`strategy-acct-risk-${group.strategy_id}-${r.accountId}`}
                value={r.risk}
                disabled={!r.enabled || r.saving}
                onChange={e => persist(i, { risk: parseFloat(e.target.value) })}
                style={{ background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: r.enabled ? 'var(--pp-text)' : 'var(--pp-text-3)', padding: '5px 8px', fontSize: 11, cursor: r.enabled ? 'pointer' : 'not-allowed' }}>
                {MULTIPLIERS.map(m => <option key={m} value={m}>{m}×</option>)}
              </select>
              {/* Per-account opt-in toggle */}
              <button data-testid={`strategy-acct-toggle-${group.strategy_id}-${r.accountId}`}
                onClick={() => persist(i, { enabled: !r.enabled })}
                disabled={r.saving}
                style={{ width: 36, height: 20, background: r.enabled ? 'rgba(0,255,148,0.3)' : 'var(--pp-border)',
                  border: `1px solid ${r.enabled ? 'var(--pp-success)' : 'var(--pp-border-active)'}`,
                  borderRadius: 10, cursor: r.saving ? 'wait' : 'pointer', position: 'relative', padding: 0,
                  opacity: r.saving ? 0.5 : 1 }}>
                <div style={{ width: 14, height: 14, borderRadius: '50%', background: r.enabled ? 'var(--pp-success)' : 'var(--pp-text-3)',
                  position: 'absolute', top: 2, left: r.enabled ? 18 : 2, transition: 'left 150ms' }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Copy Dashboard ────────────────────────────────────────────────────────
export default function CopyDashboardPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts]     = useState([]);
  const [performance, setPerformance] = useState(null);
  const [executions, setExecutions] = useState([]);
  const [openPositions, setOpenPositions] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [posLoading, setPosLoading] = useState(false);
  const [portfolio, setPortfolio]   = useState(null);
  const [closedTrades, setClosedTrades]     = useState([]);
  const [closedTradesPage, setClosedTradesPage]   = useState(1);
  const [closedTradesHasMore, setClosedTradesHasMore] = useState(false);
  const [closedTradesLoading, setClosedTradesLoading] = useState(false);
  const [closedAccountTab, setClosedAccountTab] = useState('ALL');
  const [expandedTradeKey,  setExpandedTradeKey]  = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [linked, perf, execs, port] = await Promise.all([
          api.getLinkedAccountsWithStrategies().catch(() => ({ accounts: [] })),
          api.getFollowerPerformance('advanced', 'all').catch(() => null),
          api.getFollowerExecutions(1, 10).catch(() => ({ executions: [] })),
          api.getPortfolioEquity().catch(() => null),
        ]);
        setAccounts(linked.accounts || []);
        setPerformance(perf);
        setExecutions(execs?.executions || []);
        setPortfolio(port);
      } catch (e) { console.error(e); }
      setLoading(false);

      // Load open positions separately
      setPosLoading(true);
      api.getFollowerOpenPositions()
        .then(data => setOpenPositions(data?.accounts || []))
        .catch(() => {})
        .finally(() => setPosLoading(false));

      // Closed trades (first page)
      setClosedTradesLoading(true);
      api.getClosedTrades(1, 25)
        .then(r => { setClosedTrades(r?.trades || []); setClosedTradesHasMore(!!r?.has_more); })
        .catch(() => {})
        .finally(() => setClosedTradesLoading(false));
    };
    load();
  }, []);

  const loadMoreClosed = async () => {
    setClosedTradesLoading(true);
    try {
      const next = closedTradesPage + 1;
      const r = await api.getClosedTrades(next, 25);
      setClosedTrades(prev => [...prev, ...(r?.trades || [])]);
      setClosedTradesPage(next);
      setClosedTradesHasMore(!!r?.has_more);
    } catch {}
    setClosedTradesLoading(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await api.syncLinkedBrokers();
      const queued = r?.brokers_queued || {};
      const summary = Object.entries(queued)
        .filter(([, n]) => n > 0)
        .map(([k, n]) => `${n} ${k}`)
        .join(', ');
      toast.success(`Sync queued${summary ? `: ${summary}` : ''}. Refreshing in ~30s…`);
      // Reload key data after ~30s once the first round of scrapes complete
      setTimeout(async () => {
        try {
          const [linked, port] = await Promise.all([
            api.getLinkedAccountsWithStrategies().catch(() => ({ accounts: [] })),
            api.getPortfolioEquity().catch(() => null),
          ]);
          setAccounts(linked.accounts || []);
          setPortfolio(port);
          const open = await api.getFollowerOpenPositions().catch(() => null);
          setOpenPositions(open?.accounts || []);
          const closed = await api.getClosedTrades(1, 25).catch(() => null);
          if (closed) {
            setClosedTrades(closed.trades || []);
            setClosedTradesPage(1);
            setClosedTradesHasMore(!!closed.has_more);
          }
          setLastSyncedAt(new Date());
        } catch (e) { /* silent */ }
      }, 30_000);
    } catch (e) {
      toast.error(e.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
      <div style={{ width: 32, height: 32, border: '2px solid var(--pp-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  );

  const totalOpenProfit = openPositions.reduce((s, a) => s + (a.open_profit || 0), 0);
  const totalOpenPos    = openPositions.reduce((s, a) => s + (a.positions_count || 0), 0);
  const totalPnl = (performance?.total_pnl || 0) + totalOpenProfit;
  const pc = v => v >= 0 ? 'var(--pp-success)' : 'var(--pp-error)';
  const ps = v => v >= 0 ? '+' : '';

  return (
    <div className="page-container">
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)', position: 'sticky', top: 0, background: 'var(--pp-bg)', zIndex: 20 }}>
        <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }} data-testid="copy-dashboard-back-button"><ArrowLeft size={20} /></button>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: 1 }}>COPY DASHBOARD</h1>
        <button
          onClick={() => setScheduleModalOpen(true)}
          data-testid="copy-dashboard-schedule-button"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: '1px solid var(--pp-border)',
            color: 'var(--pp-accent)', padding: '6px 12px', fontSize: 11,
            fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
            cursor: 'pointer', borderRadius: 0,
          }}
          title="Set which days of the week each account should copy trades">
          📅 Days
        </button>
        <button
          onClick={handleSync}
          disabled={syncing}
          data-testid="copy-dashboard-sync-button"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: syncing ? 'var(--pp-overlay)' : 'transparent',
            border: '1px solid var(--pp-border)',
            color: syncing ? 'var(--pp-text-3)' : 'var(--pp-accent)',
            padding: '6px 12px', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            cursor: syncing ? 'not-allowed' : 'pointer', borderRadius: 0,
          }}
          title={lastSyncedAt ? `Last synced: ${lastSyncedAt.toLocaleTimeString()}` : 'Sync all linked brokers'}>
          <RefreshCw size={13} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
          {syncing ? 'Syncing…' : 'Sync Brokers'}
        </button>
      </div>
      {scheduleModalOpen && <CopyScheduleModal onClose={() => setScheduleModalOpen(false)} accounts={accounts} />}

      {/* Summary row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderBottom: '1px solid var(--pp-border)' }}>
        {[
          { l: 'Total P&L',    v: `${ps(totalPnl)}$${totalPnl.toFixed(2)}`,       c: pc(totalPnl) },
          { l: 'Open Positions', v: totalOpenPos,                                  c: totalOpenPos > 0 ? 'var(--pp-accent)' : 'var(--pp-text)' },
          { l: 'Win Rate',     v: `${performance?.win_rate || 0}%` },
        ].map((s, i) => (
          <div key={i} style={{ padding: 14, borderRight: i < 2 ? '1px solid var(--pp-border)' : 'none', textAlign: 'center' }}>
            <p className="mono" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: s.c || 'var(--pp-text)' }}>{s.v}</p>
            <p className="section-label" style={{ marginTop: 4 }}>{s.l}</p>
          </div>
        ))}
      </div>

      <div style={{ padding: '14px 16px 0' }}>

        {/* ── Portfolio Equity (total only — full breakdown lives on Account Portfolio) ───── */}
        {portfolio && portfolio.account_count > 0 && (
          <div style={{ marginBottom: 16, background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>💼 Portfolio Equity</p>
              <p style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>{portfolio.account_count} linked account{portfolio.account_count === 1 ? '' : 's'} · live from broker APIs</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p className="mono" style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>${Number(portfolio.total_equity || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              <p className="mono" style={{ margin: 0, fontSize: 10, color: pc(portfolio.total_open_profit || 0), fontWeight: 700 }}>{ps(portfolio.total_open_profit || 0)}${(portfolio.total_open_profit || 0).toFixed(2)} open</p>
            </div>
            <button
              data-testid="see-detailed-portfolio"
              onClick={() => navigate('/account-portfolio')}
              style={{ background: 'rgba(41,121,255,0.10)', border: '1px solid rgba(41,121,255,0.35)', color: 'var(--pp-accent)', fontSize: 10, padding: '8px 12px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
              SEE DETAIL ▸
            </button>
          </div>
        )}

        {/* ── Open Positions ─────────────────────────────────────────────── */}
        {(posLoading || openPositions.length > 0) && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>📊 Open Positions</p>
              {totalOpenProfit !== 0 && (
                <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: pc(totalOpenProfit) }}>
                  {ps(totalOpenProfit)}${totalOpenProfit.toFixed(2)}
                </span>
              )}
            </div>
            {posLoading ? (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ width: 14, height: 14, border: '2px solid var(--pp-accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>Syncing with broker accounts…</span>
              </div>
            ) : openPositions.map(acct => (
              acct.positions?.length > 0 && (
                <div key={acct.connection_id} style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', marginBottom: 8 }}>
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--pp-border)', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--pp-accent)' }}>{acct.account_label}</span>
                    <span className="mono" style={{ fontSize: 12, color: pc(acct.open_profit) }}>{ps(acct.open_profit)}${acct.open_profit?.toFixed(2)}</span>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 360 }}>
                      <thead>
                        <tr>{['Symbol','Dir', acct.asset_class === 'futures' ? 'Contracts' : 'Lots', 'Entry','P&L'].map(h => (
                          <th key={h} style={{ color: 'var(--pp-text-3)', fontSize: 9, fontWeight: 700, padding: '5px 8px', textAlign: 'center', borderBottom: '1px solid var(--pp-border)', letterSpacing: '0.1em' }}>{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {acct.positions.map((p, i) => {
                          const isBuy = (p.type || '').toUpperCase().includes('BUY');
                          const pnl   = Number(p.profit || 0);
                          const unitLabel = acct.asset_class === 'futures' ? 'ct' : 'lot';
                          return (
                            <tr key={i}>
                              <td className="mono" style={{ fontSize: 11, padding: '6px 8px', textAlign: 'center', fontWeight: 700 }}>{p.symbol}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 5px', color: isBuy ? '#00FF94' : '#FF3333', background: isBuy ? 'rgba(0,255,148,0.15)' : 'rgba(255,51,51,0.15)' }}>
                                  {isBuy ? 'BUY' : 'SELL'}
                                </span>
                              </td>
                              <td className="mono" style={{ fontSize: 10, padding: '6px 8px', textAlign: 'center', color: 'var(--pp-text-3)' }} title={`${p.volume} ${unitLabel}${Number(p.volume) === 1 ? '' : 's'}`}>{p.volume}</td>
                              <td className="mono" style={{ fontSize: 10, padding: '6px 8px', textAlign: 'center' }}>{p.open_price?.toFixed?.(4) ?? '—'}</td>
                              <td className="mono" style={{ fontSize: 11, padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: pc(pnl) }}>{ps(pnl)}${pnl.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            ))}
          </div>
        )}

        {/* ── Subscribed Strategies, grouped strategy-first ───────────────── */}
        {/* Each strategy expands to show ALL of the student's linked accounts
            with a per-account toggle (default OFF — opt-in) and a per-account
            risk multiplier. The strategy-level PAUSE ALL button hard-stops
            copying across every account at once. */}
        {(() => {
          // Build strategy → [accounts] map from the per-account
          // strategy_settings the backend already returns.
          const byStrategy = new Map();
          accounts.forEach(acct => {
            (acct.strategy_settings || []).forEach(s => {
              if (!byStrategy.has(s.strategy_id)) {
                byStrategy.set(s.strategy_id, {
                  strategy_id:   s.strategy_id,
                  strategy_name: s.strategy_name,
                  instrument:    s.instrument,
                  rows: [],
                });
              }
              byStrategy.get(s.strategy_id).rows.push({ acct, s });
            });
          });
          const strategyGroups = Array.from(byStrategy.values());

          if (accounts.length === 0) {
            return (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 24, textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--pp-text-3)', margin: '0 0 12px' }}>No broker accounts linked yet</p>
                <button onClick={() => navigate('/link-broker')} className="btn-primary" style={{ padding: '10px 20px' }}>Link Broker Account</button>
              </div>
            );
          }
          if (strategyGroups.length === 0) {
            return (
              <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 24, textAlign: 'center' }}>
                <p style={{ fontSize: 13, color: 'var(--pp-text-3)', margin: '0 0 12px' }}>You're not subscribed to any strategies yet.</p>
                <button onClick={() => navigate('/strategies')} className="btn-primary" style={{ padding: '10px 20px' }}>Browse Strategies</button>
              </div>
            );
          }

          return (
            <>
              <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 700 }}>📡 Subscribed Strategies</p>
              <p style={{ margin: '0 0 12px', fontSize: 11, color: 'var(--pp-text)', lineHeight: 1.6 }}>
                Each strategy lists every account you've linked. <strong style={{ color: 'var(--pp-warning)' }}>By default no account copies</strong> — turn on each account you want to mirror the strategy on. Risk multipliers apply per account.
              </p>
              {strategyGroups.map(grp => (
                <StrategyGroup key={grp.strategy_id} group={grp} />
              ))}
            </>
          );
        })()}

        {/* ── Closed Trades History ──────────────────────────────────────── */}
        {(() => {
          // Build account tabs from unique labels present in closedTrades
          const uniqLabels = Array.from(new Set(closedTrades.map(t => t.account_label || '—').filter(Boolean)));
          const tabs = ['ALL', ...uniqLabels];
          const filtered = closedAccountTab === 'ALL'
            ? closedTrades
            : closedTrades.filter(t => (t.account_label || '—') === closedAccountTab);
          const fmtNum = (v) => v != null && !isNaN(Number(v))
            ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 6 })
            : '—';
          const fmtDate = (iso) => iso
            ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
            : '—';
          return (
            <div style={{ marginTop: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>📜 Closed Trades History</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {filtered.length > 0 && <span style={{ fontSize: 10, color: 'var(--pp-text-3)' }}>{filtered.length} trade{filtered.length === 1 ? '' : 's'}</span>}
                  <button
                    data-testid="sync-closed-trades-now"
                    onClick={async (e) => {
                      const btn = e.currentTarget;
                      const old = btn.textContent;
                      btn.disabled = true; btn.textContent = '⏳ SYNCING';
                      try {
                        const r = await api.syncClosedTradesNow(90);
                        toast.success(`${r.message}`);
                        setTimeout(async () => {
                          try {
                            const latest = await api.getClosedTrades(1, 25);
                            setClosedTrades(latest.trades || []);
                            setClosedPage(latest.page || 1);
                            setClosedHasMore(latest.has_more || false);
                          } catch (_) {}
                          btn.disabled = false; btn.textContent = old;
                        }, 45000);
                      } catch (err) {
                        toast.error(err.message);
                        btn.disabled = false; btn.textContent = old;
                      }
                    }}
                    style={{ background: 'rgba(0,255,148,0.08)', border: '1px solid #00FF94', color: '#00FF94', padding: '4px 10px', fontSize: 10, cursor: 'pointer', fontWeight: 800, letterSpacing: '0.08em' }}
                  >⟲ SYNC NOW</button>
                </div>
              </div>

              {/* Account tabs */}
              {tabs.length > 1 && (
                <div style={{ display: 'flex', gap: 0, marginBottom: 10, borderBottom: '1px solid var(--pp-border)', overflowX: 'auto' }}>
                  {tabs.map(tab => {
                    const count = tab === 'ALL'
                      ? closedTrades.length
                      : closedTrades.filter(t => (t.account_label || '—') === tab).length;
                    const isActive = closedAccountTab === tab;
                    return (
                      <button
                        key={tab}
                        data-testid={`closed-tab-${tab.replace(/\s+/g,'-')}`}
                        onClick={() => { setClosedAccountTab(tab); setExpandedTradeKey(null); }}
                        style={{
                          background: isActive ? 'var(--pp-surface)' : 'transparent',
                          border: 'none',
                          borderBottom: isActive ? '2px solid var(--pp-accent)' : '2px solid transparent',
                          color: isActive ? 'var(--pp-text)' : 'var(--pp-text-3)',
                          padding: '8px 14px',
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: '0.08em',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          fontFamily: 'inherit',
                        }}
                      >
                        {tab === 'ALL' ? 'ALL' : tab.toUpperCase()}
                        <span style={{ marginLeft: 6, color: 'var(--pp-text-3)', fontSize: 9, fontWeight: 600 }}>({count})</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {closedTradesLoading && filtered.length === 0 ? (
                <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ width: 14, height: 14, border: '2px solid var(--pp-accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>Loading closed trades from your broker accounts…</span>
                </div>
              ) : filtered.length === 0 ? (
                <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 18, textAlign: 'center' }}>
                  <p style={{ fontSize: 12, color: 'var(--pp-text-3)', margin: 0 }}>No closed trades {closedAccountTab === 'ALL' ? 'yet' : `for ${closedAccountTab}`}.</p>
                </div>
              ) : (
                <div data-testid="closed-trades-table" style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)' }}>
                  {/* Compact list — clickable rows reveal details */}
                  {filtered.map((t, i) => {
                    const key = `${t.source}-${t.deal_id || t.closed_at || i}-${i}`;
                    const pnl = Number(t.pnl || 0);
                    const sideUp = (t.side || '').toString().toUpperCase();
                    const isBuy = sideUp.includes('BUY') || sideUp === 'LONG';
                    const isOpen = expandedTradeKey === key;
                    const platColor = t.platform?.startsWith('Bybit') ? '#F7A600' : t.platform?.includes('MT') ? '#F28E00' : 'var(--pp-text-2)';
                    return (
                      <div key={key} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        {/* Compact row */}
                        <button
                          data-testid={`closed-trade-row-${i}`}
                          onClick={() => setExpandedTradeKey(isOpen ? null : key)}
                          style={{ width: '100%', background: isOpen ? 'var(--pp-overlay)' : 'transparent', border: 'none', display: 'grid', gridTemplateColumns: 'auto 1fr auto auto 20px', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', color: 'var(--pp-text)', textAlign: 'left', fontFamily: 'inherit' }}
                        >
                          <span style={{ fontSize: 9, fontWeight: 800, padding: '3px 7px', color: isBuy ? '#00FF94' : '#FF3333', background: isBuy ? 'rgba(0,255,148,0.12)' : 'rgba(255,51,51,0.12)', letterSpacing: '0.05em' }}>
                            {isBuy ? 'BUY' : 'SELL'}
                          </span>
                          <span className="mono" style={{ fontSize: 12, fontWeight: 700 }}>{t.symbol || '—'}</span>
                          <span style={{ fontSize: 9, color: platColor, fontWeight: 700, letterSpacing: '0.05em' }}>{t.platform || '—'}</span>
                          <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: pc(pnl), minWidth: 70, textAlign: 'right' }}>{ps(pnl)}${Math.abs(pnl).toFixed(2)}</span>
                          {isOpen
                            ? <ChevronDown size={14} style={{ color: 'var(--pp-text-3)' }} />
                            : <ChevronRight size={14} style={{ color: 'var(--pp-text-3)' }} />}
                        </button>
                        {/* Expandable details */}
                        {isOpen && (
                          <div data-testid={`closed-trade-details-${i}`} style={{ padding: '8px 14px 14px', background: 'var(--pp-overlay)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                            <Detail label="Closed At"    value={fmtDate(t.closed_at)} />
                            <Detail label="Opened At"    value={fmtDate(t.opened_at || t.executed_at)} />
                            <Detail label="Account"      value={t.account_label || '—'} />
                            <Detail label={t.asset_class === 'futures' ? 'Contracts' : t.asset_class === 'crypto' ? 'Contracts (Crypto)' : 'Lots'} value={fmtNum(t.size)} mono />
                            <Detail label="Entry Price"  value={fmtNum(t.entry)} mono />
                            <Detail label="Exit Price"   value={fmtNum(t.exit)}  mono />
                            <Detail label="Source"       value={t.source || '—'} />
                            <Detail label="Leverage"     value={t.leverage != null ? `${t.leverage}x` : '—'} />
                            <Detail label="Deal / Order ID" value={t.deal_id || t.order_id || '—'} mono small />
                            {t.comment && <Detail label="Comment" value={t.comment} />}
                            {t.market_type && <Detail label="Market" value={t.market_type} />}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {closedTradesHasMore && (
                    <div style={{ padding: 10, textAlign: 'center' }}>
                      <button data-testid="closed-trades-load-more" onClick={loadMoreClosed} disabled={closedTradesLoading} style={{ background: 'transparent', border: '1px solid var(--pp-border)', color: 'var(--pp-text-2)', fontSize: 11, padding: '6px 18px', cursor: 'pointer', fontWeight: 700, letterSpacing: '0.08em' }}>{closedTradesLoading ? 'Loading…' : 'LOAD MORE'}</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Recent Executions (small dispatch-event log, non-trade) ───── */}
        {executions.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <p style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700 }}>🕑 Recent Executions</p>
            {executions.slice(0, 5).map((exec, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--pp-border)' }}>
                <div>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{exec.symbol || 'Unknown'}</p>
                  <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)' }}>{exec.executed_at?.slice(0, 10)}</p>
                </div>
                <p className="mono" style={{ margin: 0, fontWeight: 700, color: pc(exec.pnl || 0) }}>
                  {ps(exec.pnl || 0)}${(exec.pnl || 0).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}
